import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { useOfflineModeManager, useRequiredCurrentUser } from 'FrontRoyalAngular';
import { useErrorLogService } from 'ErrorLogging';
import { type LocaleAngularObject } from 'Locale';
import { type PreferenceSelectOption } from '../editPreferences.types';
import { useUpdatePreference } from '../useUpdatePreference';
import { PreferenceSwitchRow } from './PreferenceSwitchRow';
import { PreferenceSelectRow } from './PreferenceSelectRow';
import { ClearLocalDatabaseRow } from './ClearLocalDatabaseRow';
import { TestAppReviewRow } from './TestAppReviewRow';
import { NotificationPreferences } from './NotificationPreferences';

const DECIMAL_DELIM_OPTIONS: PreferenceSelectOption[] = [
    { title: '1,234.56', value: '.' },
    { title: '1.234,56', value: ',' },
];

// oxlint-disable-next-line max-lines-per-function -- container coordinates several controls and their side-effects
export function EditPreferences() {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const $injector = useAngularContext();
    const offlineModeManager = useOfflineModeManager();
    const updatePreference = useUpdatePreference();
    const ErrorLogService = useErrorLogService();

    // Local, optimistic copies of each preference (mirrors the old `scope.preferences`).
    // We flip these immediately on change for snappy UI and revert them if the save fails.
    const [soundEnabled, setSoundEnabled] = useState(currentUser.prefSoundEnabled);
    const [offlineMode, setOfflineMode] = useState(currentUser.prefOfflineMode);
    const [keyboardShortcuts, setKeyboardShortcuts] = useState(currentUser.prefKeyboardShortcuts);
    const [decimalDelim, setDecimalDelim] = useState(currentUser.prefDecimalDelim);
    const [locale, setLocale] = useState(currentUser.prefLocale);

    const [offlineModeSupported, setOfflineModeSupported] = useState(false);
    // Changing the locale reloads the route, which takes a few seconds. Hide the form
    // while it happens so the user can't start editing something else mid-reload.
    const [changingLocale, setChangingLocale] = useState(false);

    useEffect(() => {
        let active = true;
        offlineModeManager.isOfflineModeSupported().then(supported => {
            if (active) setOfflineModeSupported(supported);
        });
        return () => {
            active = false;
        };
    }, [offlineModeManager]);

    // availablePreferenceLocales() returns a static list (it ignores its argument), so this
    // only needs to be computed once — it does not need to react to currentUser.
    const localeOptions = useMemo<PreferenceSelectOption[]>(() => {
        const localeService = $injector?.get<LocaleAngularObject>('Locale');
        return (localeService?.availablePreferenceLocales() ?? []).map(({ title, value }) => ({ title, value }));
    }, [$injector]);

    const failureContent = t('settings.edit_preferences.update_preference_failure');
    const successContentFor = (localeBase: string) =>
        t('settings.edit_preferences.update_preference_success', {
            title: t(`settings.edit_preferences.${localeBase}_title`),
        });

    const handleSoundChange = (value: boolean) => {
        const previousValue = soundEnabled;
        setSoundEnabled(value);

        // Sound is toggled locally regardless of the save (matches the old directive).
        const soundManager = $injector?.get<{ enabled: boolean }>('SoundManager');
        if (soundManager) soundManager.enabled = value;

        updatePreference({
            field: 'pref_sound_enabled',
            value,
            successContent: successContentFor('sound_enabled'),
            failureContent,
        }).catch(() => {
            setSoundEnabled(previousValue);
            if (soundManager) soundManager.enabled = previousValue;
        });
    };

    const handleOfflineModeChange = (value: boolean) => {
        const previousValue = offlineMode;
        setOfflineMode(value);
        updatePreference({
            field: 'pref_offline_mode',
            value,
            successContent: successContentFor('offline_mode'),
            failureContent,
            onSuccess: () => {
                if (value === false) offlineModeManager.disableOfflineMode();
            },
        }).catch(() => setOfflineMode(previousValue));
    };

    const handleKeyboardShortcutsChange = (value: boolean) => {
        const previousValue = keyboardShortcuts;
        setKeyboardShortcuts(value);
        updatePreference({
            field: 'pref_keyboard_shortcuts',
            value,
            successContent: successContentFor('keyboard_shortcuts'),
            failureContent,
        }).catch(() => setKeyboardShortcuts(previousValue));
    };

    const handleDecimalDelimChange = (value: string) => {
        const previousValue = decimalDelim;
        setDecimalDelim(value);
        updatePreference({
            field: 'pref_decimal_delim',
            value,
            successContent: successContentFor('decimal_delim'),
            failureContent,
        }).catch(() => setDecimalDelim(previousValue));
    };

    const handleLocaleChange = (value: string) => {
        const previousValue = locale;
        setLocale(value);
        setChangingLocale(true);
        updatePreference({
            field: 'pref_locale',
            value,
            successContent: successContentFor('locale'),
            failureContent,
            onSuccess: () => {
                const localeService = $injector?.get<LocaleAngularObject>('Locale');
                const learnerContentCache = $injector?.get<{ preloadStudentDashboard: () => void }>(
                    'LearnerContentCache',
                );

                // Match the original directive's order: switch the language first, then preload.
                // attemptToSetLanguage reloads the route on success (unmounting this form). If it
                // rejects (e.g. a translation bundle fails to load), un-hide the form and restore
                // the previous locale so the user isn't left staring at a blank tab.
                Promise.resolve(localeService?.attemptToSetLanguage(value, true, true)).catch(error => {
                    ErrorLogService.notify('Failed to switch language', error, { locale: value });
                    setLocale(previousValue);
                    setChangingLocale(false);
                });
                learnerContentCache?.preloadStudentDashboard();
            },
        }).catch(() => {
            setLocale(previousValue);
            setChangingLocale(false);
        });
    };

    if (changingLocale) return null;

    return (
        <div className="edit-preferences px-4 py-5 sm:px-10">
            <div>
                <PreferenceSwitchRow
                    name="pref_sound_enabled"
                    title={t('settings.edit_preferences.sound_enabled_title')}
                    description={t('settings.edit_preferences.sound_enabled_desc')}
                    descriptionExtra={
                        soundEnabled && window.CORDOVA
                            ? t('settings.edit_preferences.sound_enabled_desc_extra')
                            : undefined
                    }
                    isSelected={soundEnabled}
                    onValueChange={handleSoundChange}
                />
                {offlineModeSupported ? (
                    <PreferenceSwitchRow
                        name="pref_offline_mode"
                        title={t('settings.edit_preferences.offline_mode_title')}
                        description={t('settings.edit_preferences.offline_mode_desc')}
                        isSelected={offlineMode}
                        onValueChange={handleOfflineModeChange}
                    />
                ) : null}
                <PreferenceSwitchRow
                    name="pref_keyboard_shortcuts"
                    title={t('settings.edit_preferences.keyboard_shortcuts_title')}
                    description={t('settings.edit_preferences.keyboard_shortcuts_desc')}
                    isSelected={keyboardShortcuts}
                    onValueChange={handleKeyboardShortcutsChange}
                />
                <NotificationPreferences />
                <PreferenceSelectRow
                    title={t('settings.edit_preferences.decimal_delim_title')}
                    description={t('settings.edit_preferences.decimal_delim_desc')}
                    value={decimalDelim}
                    options={DECIMAL_DELIM_OPTIONS}
                    onValueChange={handleDecimalDelimChange}
                />
                <PreferenceSelectRow
                    title={t('settings.edit_preferences.locale_title')}
                    description={t('settings.edit_preferences.locale_desc')}
                    value={locale}
                    options={localeOptions}
                    onValueChange={handleLocaleChange}
                />
                <ClearLocalDatabaseRow />
                <TestAppReviewRow />
            </div>
        </div>
    );
}
